import dateUtils from '@/common/dateUtils';
import DB from '@/modules/fahrdienst/storeNonReactive';
import store from '@/store';

const HALF_A_DAY_IN_MILLIS = 12 * 60 * 60 * 1000;
const A_DAY_IN_MILLIS = 24 * 60 * 60 * 1000;

export default {
  getRelatedArtifacts(files, dutyTitle) {
    return files.filter((f) => f.title !== dutyTitle && f.dienst.split(',').includes(dutyTitle));
  },
  /**
   * Returns the Monday based day of week (Monday=1, Sunday=7)
   * @param {Date} date - a date
   * @returns the day of week
   */
  getDayOfWeek(date) {
    let dayOfWeek = date.getDay(); // monday=1, sunday=7
    dayOfWeek = dayOfWeek === 0 ? 7 : dayOfWeek;
    return dayOfWeek;
  },

  /**
   * Checks if day is accepted, that is within dayTypes (including) boundaries
   * + dates additionally added
   * - dates removed
   * @param {Date} date - a date formatted as ISO string
   * @param {Object} fahrdienstFile - the Fahrdienst File to check
   * @param {number} dayType - numbers 1 (=Monday) to 7 (=Sunday). From to (including).
   *                           examples:
   *                           11 = Monday only
   *                           12 = Monday to Tuesday
   *                           26 = Tuesday to Saturday
   *                           However this may also be a comma separated list of day types
   * @param {Array} additionalFahrdienstDays - list of additional dates which are valid next to the day type.
   * @param {Array} cancelledFahrdienstDays - list of days which are not valid
   * @returns true if the date is accepted
   */
  // Date saved on DB 2022-03-29
  // serialized in backend as 2022-03-28T22:00:00.000Z=new Date(2022,04,29).toISOString()
  // use in frontend by Date.parse('2022-03-28T22:00:00.000Z')
  isDayAccepted({ date, dayType, verkehrsPeriodenKalender = [], additionalFahrdienstDays = [], cancelledFahrdienstDays = [] }) {
    let dayOfWeek = this.getDayOfWeek(date);

    let dayTypes = `${dayType}`.split(',');
    let foundDayType = dayTypes.find((dayType) =>
      this.isAcceptedAtDayType({
        date,
        dayType,
        dayOfWeek,
        verkehrsPeriodenKalender,
        additionalFahrdienstDays,
        cancelledFahrdienstDays,
      })
    );
    return !!foundDayType;
  },
  /**
   * Subroutine to check for a single dayType.
   * @param date
   * @param dayType
   * @param dayOfWeek
   * @param verkehrsPeriodenKalender
   * @param additionalFahrdienstDays
   * @param cancelledFahrdienstDays
   * @returns {boolean}
   */
  isAcceptedAtDayType({ date, dayType, dayOfWeek, verkehrsPeriodenKalender = [], additionalFahrdienstDays = [], cancelledFahrdienstDays = [] }) {
    let dayTypeArray = `${dayType}`.split('').map((a) => Number(a));
    let holiday = verkehrsPeriodenKalender.find((i) => {
      return dateUtils.isSameDate(date, new Date(Date.parse(i.datum)));
    });
    if (holiday) {
      //console.debug('found holiday to be a ', holiday.verkehrsperiode);
      dayOfWeek = holiday.verkehrsperiode;
    }

    let isDayAcceptedByDayType = dayTypeArray[0] <= dayOfWeek && dayTypeArray[1] >= dayOfWeek;
    //console.debug('isDayAcceptedByDayType', isDayAcceptedByDayType);
    if (isDayAcceptedByDayType) {
      // if it is accepted, we need to check if the date has been manually removed
      let removedItem = cancelledFahrdienstDays.find((c) => dateUtils.isSameDate(date, new Date(Date.parse(c.datum))));
      //console.debug('found removed?', removedItem);
      if (removedItem) return false;
      return true;
    } else {
      // if it is not accepted, we need to check if the date has been manually added
      let added = additionalFahrdienstDays.find((c) => dateUtils.isSameDate(date, new Date(Date.parse(c.datum))));
      //console.debug('found added?', added);
      if (added) return true;
      return false;
    }
  },
  /**
   * Calculate duties with files at the given dates. Used in FahrdienstHeader.
   * @param {*} param0
   * @returns {Array} duty files valid at the given date
   */
  getDutiesAtSelectedDate_({
    date,
    dutyFilesTypeIdFk,
    fahrdienstDuties,
    files,
    instance,
    verkehrsPeriodenKalender,
    additionalFahrdienstDays,
    cancelledFahrdienstDays,
  }) {
    console.debug('getDutiesAtSelectedDate');
    let dutiesAtSelectedDate = fahrdienstDuties
      .filter((d) => d.inst === instance)
      .filter((d) => {
        let filesValidAt = this.findDutiesWithFilesValidAt({
          dutyTitle: d.title,
          dutyFilesTypeIdFk,
          date,
          instance,

          files,
          verkehrsPeriodenKalender,
          additionalFahrdienstDays,
          cancelledFahrdienstDays,
        });
        return filesValidAt.length > 0;
      });
    // remove duplicates and sort
    return this.removeDuplicateDuties(dutiesAtSelectedDate).sort(this.dutyComparator);
  },
  getDutiesAtSelectedDate({
    date,
    dutyFilesTypeIdFk,
    fahrdienstDuties,
    files,
    instance,
    verkehrsPeriodenKalender,
    additionalFahrdienstDays,
    cancelledFahrdienstDays,
    filterByDate,
  }) {
    //console.debug('getDutiesAtSelectedDate');

    let filesValidAt = this.findDutiesWithFilesValidAt({
      filterByDutyName: false,
      dutyTitle: 'NOT APPLICABLE',
      dutyFilesTypeIdFk,
      date,
      instance,
      fahrdienstDuties,
      files,
      verkehrsPeriodenKalender,
      additionalFahrdienstDays,
      cancelledFahrdienstDays,
      filterbyPriority: false,
      filterByDate,
    });

    //console.debug('getDutiesAtSelectedDate files', files, filesValidAt);

    let dutyMap = {};
    let fahrdienstDutiesByInstAndTitle = {};

    fahrdienstDuties.forEach((d) => (fahrdienstDutiesByInstAndTitle[`${d.inst}.${d.title}`] = d));
    filesValidAt
      .map((f) => f.dienst)
      .join(',')
      .split(',')
      .forEach((dutyName) => {
        dutyMap[dutyName.trim()] = fahrdienstDutiesByInstAndTitle[`${instance}.${dutyName.trim()}`];
      }); //avoids duplicates

    // remove duplicates and sort
    let duties = Object.keys(dutyMap).map((k) => dutyMap[k]);
    return duties.sort(this.dutyComparator);
  },
  removeDuplicateDuties(dutyList) {
    let dutyMap = {};
    dutyList.forEach((d) => (dutyMap[`${d.inst}.${d.title}`] = d));

    return Object.keys(dutyMap).map((duty) => dutyMap[duty]);
  },
  dutyComparator(a, b) {
    let aAsNumber = Number(a.title);
    let bAsNumber = Number(b.title);
    if (isNaN(aAsNumber) || isNaN(bAsNumber)) {
      return a.title > b.title ? -1 : 1;
    }
    // console.debug('Compare a', a.title, 'with b', b.title, aAsNumber > bAsNumber ? -1 : 1);
    return aAsNumber > bAsNumber ? -1 : 1;
  },
  /**
   * Find all duties having documents valid for the selected day (@param date), dayType (stored in files as tagtyp),
   * verkehrsperiodenkalender, additionalFahrdienstDays,cancelledFahrdienstDays and the current instance.
   * Check if a fahrdienst file exists which is valid at the given date. The file is selected in case
   * the file title field equals the duty title (and not as expected by a file.dienst.includes(dutyTitle) condition, which would result in multiple files)
   */
  /**
   *
   * @param {Date} date - the date to check if files are valid at that given date
   * @param {String} dutyTitle - the title of the duty (which might be used for selection by file.title)
   * @param {Boolean} filterByDutyName - true to indicate that the files need to be filtered by dienst name
   * @param {Boolean} dutyTitleMustMatch - true to indicate that the title must match the dutyFile.dienst
   * @param dutyFilesTypeIdFk
   * @param instance
   * @param files
   * @param verkehrsPeriodenKalender - optional parameter (mainly provided for testing purposes)
   * @param additionalFahrdienstDays - optional parameter (mainly provided for testing purposes)
   * @param cancelledFahrdienstDays - optional parameter (mainly provided for testing purposes)
   * @returns
   */
  findDutiesWithFilesValidAt({
    filterByDutyName = true,
    date,
    dutyTitle,
    dutyTitleMustMatch = true,
    dutyFilesTypeIdFk = 0,
    instance,
    files,
    verkehrsPeriodenKalender = DB.verkehrsPeriodenKalender,
    additionalFahrdienstDays = DB.additionalFahrdienstDays,
    cancelledFahrdienstDays = DB.cancelledFahrdienstDays,
    filterbyPriority = true,
    fahrdienstTimetableDayHours,
    filterByDate = false,
  }) {
    if (!fahrdienstTimetableDayHours) {
      fahrdienstTimetableDayHours = store.getters['fahrdienst/fahrdienstTimetableDayHours'] || 24;
    }
    console.debug('findDutiesWithFilesValidAt', date);
    let offsetHours = fahrdienstTimetableDayHours - 24;
    const dateNoHoursMinutes = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    console.debug('dateNoHoursMinutes', dateNoHoursMinutes.toISOString());
    // console.debug('dutyFiles after instance and dienst/title filtering', dutyTitle, dutyFiles);
    const typeIds = Array.isArray(dutyFilesTypeIdFk) ? dutyFilesTypeIdFk : [dutyFilesTypeIdFk];
    let dutyFiles = files
      .filter((f) => typeIds.includes(f.typeIdFk))
      .filter((f) => {
        return filterByDate ? dateUtils.isBetweenIncludingEndDate(dateNoHoursMinutes, f.anzeige_von, f.anzeige_bis, offsetHours) : true;
      });
    if (filterByDutyName) {
      dutyFiles = dutyFiles.filter((f) => {
        if (dutyTitleMustMatch) {
          // BOGG selects files with an exact match of the title instead of like expected by a f.dienst.includes(dutyTitle)
          let dienste = f.dienst ? f.dienst.split(',').map((d) => d.trim()) : [];
          return f.inst == instance && (f.dienst === 'Alle' || dutyTitle == f.title || dienste.includes(dutyTitle));
        } else {
          return f.inst == instance;
        }
      });
    }

    // console.debug('dutyFiles after filtering if excludeDuties', excludeDuties, dutyFiles);
    if (filterByDate) {
      dutyFiles = dutyFiles.filter((f) =>
        this.isDayAccepted({
          date,
          dayType: f.tagtyp,
          verkehrsPeriodenKalender,
          additionalFahrdienstDays: additionalFahrdienstDays.filter((d) => d.fahrdienstFileIdFk === f.id),
          cancelledFahrdienstDays: cancelledFahrdienstDays.filter((d) => d.fahrdienstFileIdFk == f.id),
        })
      );
    }

    if (!filterbyPriority) {
      return dutyFiles;
    }

    //console.warn('dutyFiles after filtering of isDayAccepted', dutyFiles);

    // Group dutyFiles by their typeIdFk
    const dutyFilesByType = dutyFiles.reduce((groups, file) => {
      (groups[file.typeIdFk] = groups[file.typeIdFk] || []).push(file);
      return groups;
    }, {});

    // Select files with the highest priority within each typeIdFk group
    const maxPriorityDutyFiles = Object.values(dutyFilesByType).flatMap((filesOfSameType) => {
      const priorities = filesOfSameType.map((file) => file.priority ?? -1);
      const highestPriority = Math.max(...priorities);

      if (highestPriority < 0) {
        return [];
      }

      return filesOfSameType.filter((file) => file.priority === highestPriority);
    });

    return maxPriorityDutyFiles;
  },
};
